import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import { joinFishbowl, leaveFishbowl } from "../lib/api";
import { friendly } from "../lib/errors";

const DAILY_LIMIT = 10;

/** Group Relay queue: join, wait for a room of 8, auto-enter when it forms. */
export default function RelayGroupQueue() {
  const { uid } = useAuth();
  const navigate = useNavigate();
  const [queued, setQueued] = useState<boolean | null>(null);
  const [countToday, setCountToday] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(doc(db, "users", uid), (snap) => {
      const d = snap.data();
      const today = new Date().toISOString().slice(0, 10);
      setCountToday(d?.fishbowlCountDate === today ? (d?.fishbowlCountToday as number ?? 0) : 0);
      if (d?.activeFishbowlId) navigate(`/relay/room/${d.activeFishbowlId}`, { replace: true });
    });
  }, [uid, navigate]);

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(
      doc(db, "fishbowlQueue", uid),
      (snap) => setQueued(snap.exists()),
      () => setQueued(false),
    );
  }, [uid]);

  async function join() {
    setBusy(true);
    setError(null);
    try {
      await joinFishbowl();
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  async function leave() {
    setBusy(true);
    setError(null);
    try {
      await leaveFishbowl();
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <Link to="/relay" className="chat-back" style={{ textDecoration: "none" }}>‹ Relay</Link>
      <h1>Group</h1>
      <p className="muted">
        Live 8-person rooms, 15 minutes. You rotate partners each round, play a quick game
        together, then the whole room opens up to chat. People you clicked with get pinned
        to the front of your Discover for 24 hours.
      </p>

      {queued === null ? (
        <div className="splash" style={{ height: 160 }}><div className="spinner" /></div>
      ) : queued ? (
        <div className="relay-queue-box">
          <div className="pulse-ring" />
          <h2 style={{ marginTop: 18 }}>Finding your room…</h2>
          <p className="muted">
            We're assembling 8 compatible people. You'll drop in automatically — feel free
            to browse Discover while you wait.
          </p>
          <button className="btn btn-secondary" disabled={busy} onClick={() => void leave()}>
            Leave the queue
          </button>
        </div>
      ) : (
        <>
          <div className="relay-how">
            <div className="how-step"><span className="how-num">1</span> Get matched into a room of 8</div>
            <div className="how-step"><span className="how-num">2</span> Play rounds with a new partner each time</div>
            <div className="how-step"><span className="how-num">3</span> Final 5 minutes: open room chat</div>
          </div>
          <button className="btn" disabled={busy || countToday >= DAILY_LIMIT} onClick={() => void join()}>
            {busy ? "Joining…" : "Join a Group Relay"}
          </button>
          <p className="muted center" style={{ fontSize: 13 }}>
            {countToday >= DAILY_LIMIT
              ? "You've used today's 10 Relays — resets at midnight UTC."
              : `${DAILY_LIMIT - countToday} of ${DAILY_LIMIT} Relays left today`}
          </p>
        </>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}
